import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Briefcase, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_LABEL = { company_admin: 'Admin', dispatcher: 'Dispatcher', accountant: 'Accountant' };
const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StaffRow = ({ member, onSaved }) => {
  const p = member.pay_profile;
  const [salary, setSalary] = useState(p ? String(p.weekly_salary) : '');
  const [active, setActive] = useState(p ? p.is_active : true);
  const [saving, setSaving] = useState(false);
  const dirty = p ? (Number(salary) !== p.weekly_salary || active !== p.is_active) : salary !== '';

  const save = async () => {
    const amount = Number(salary);
    if (!amount || amount <= 0) { toast.error('Enter a weekly salary greater than $0'); return; }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/payroll/staff-profiles/${member.user_id}`, { weekly_salary: amount, is_active: active }, { withCredentials: true });
      onSaved(member.user_id, r.data.pay_profile);
      toast.success(`${member.name}: ${active ? 'salary saved' : 'excluded from payroll'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save salary');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid={`staff-salary-row-${member.user_id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900 truncate">{member.name}</p>
        <p className="text-xs text-slate-500 truncate">{ROLE_LABEL[member.role] || member.role} · {member.email}</p>
      </div>
      <div className="flex items-center gap-2 sm:gap-3">
        <label className="flex items-center gap-1.5 text-xs text-slate-600 select-none">
          <input data-testid={`staff-salary-active-${member.user_id}`} type="checkbox" className="h-4 w-4 accent-[#002FA7]" checked={active} onChange={(e) => setActive(e.target.checked)} />
          On payroll
        </label>
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
          <input
            data-testid={`staff-salary-input-${member.user_id}`}
            type="number" min="0" step="0.01" inputMode="decimal" placeholder="0.00"
            value={salary} onChange={(e) => setSalary(e.target.value)}
            className="w-32 rounded-md border border-slate-300 pl-5 pr-2 py-1.5 text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30"
          />
        </div>
        <span className="text-xs text-slate-400 whitespace-nowrap">/ week</span>
        <button
          data-testid={`staff-salary-save-${member.user_id}`}
          type="button" onClick={save} disabled={saving || !dirty}
          className="inline-flex items-center gap-1 rounded-md bg-[#002FA7] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
        </button>
      </div>
    </div>
  );
};

export const StaffSalaryPanel = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payroll/staff-profiles`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ staff: [] }));
  }, []);

  const onSaved = (userId, profile) => setData((d) => ({ ...d, staff: d.staff.map((m) => (m.user_id === userId ? { ...m, pay_profile: profile } : m)) }));
  const onPayroll = (data?.staff || []).filter((m) => m.pay_profile?.is_active);
  const weekly = onPayroll.reduce((s, m) => s + (m.pay_profile?.weekly_salary || 0), 0);

  return (
    <div data-testid="staff-salary-panel" className="mt-6 rounded-md border border-slate-200 bg-white">
      <button type="button" data-testid="staff-salary-toggle" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 px-4 sm:px-6 py-4 text-left">
        <div className="flex items-center gap-3 min-w-0">
          <Briefcase className="h-5 w-5 text-[#002FA7] shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Office staff salaries</p>
            <p className="text-xs text-slate-500 truncate" data-testid="staff-salary-summary">
              {data ? `${onPayroll.length} on payroll · ${fmt(weekly)} / week added to each run` : 'Loading…'}
            </p>
          </div>
        </div>
        <span className="text-xs font-semibold text-[#002FA7]">{open ? 'Hide' : 'Manage'}</span>
      </button>
      {open && (
        <div className="px-4 sm:px-6 pb-4">
          <p className="text-xs text-slate-500 mb-2">Fixed weekly salary for admins, dispatchers and accountants. Salaried staff get a pay statement in every weekly run; adjustments, bonuses and deductions are added on the payroll detail page. Drivers are paid through their driver pay profile.</p>
          {(data?.staff || []).length === 0 && <p className="text-sm text-slate-500 py-3">No office staff on this company yet — invite them from the Team page.</p>}
          {(data?.staff || []).map((m) => <StaffRow key={m.user_id} member={m} onSaved={onSaved} />)}
        </div>
      )}
    </div>
  );
};

export default StaffSalaryPanel;
